import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconCalendarWeek, IconChevronLeft, IconChevronRight, IconPencil } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useDisplayTimezone } from '../context/TimeModeContext';
import {
  toLocalTime,
  getMondayOfWeek,
  getWeekRangeLabelFor,
  pairIntoSessions,
  shiftDateString,
  DAY_ANCHOR_TZ,
} from '../utils/time';
import Layout from '../components/Layout';
import EditNoteModal from '../components/EditNoteModal';

export default function WeeklyNotes() {
  const { currentUser, timelogs } = useApp();
  const { showError } = useToast();
  const displayTz = useDisplayTimezone(currentUser);
  const [copied, setCopied] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // { entry, contextLabel } | null

  // The week itself (Monday–Friday) is always CST-anchored, regardless of
  // the CST/local toggle — see DAY_ANCHOR_TZ.
  const thisWeekMonday = useMemo(() => getMondayOfWeek(undefined, DAY_ANCHOR_TZ), []);
  const [selectedMonday, setSelectedMonday] = useState(thisWeekMonday);

  const weekRangeLabel = useMemo(
    () => getWeekRangeLabelFor(selectedMonday),
    [selectedMonday]
  );

  // Pair ALL of this person's logs into sessions first (not just this
  // week's), so a session that starts the Friday before this week or ends
  // the Monday after it still gets attributed to the correct day rather
  // than showing up as a false orphan at either edge of the week.
  const weekDayKeys = useMemo(
    () => Array.from({ length: 5 }, (_, i) => shiftDateString(selectedMonday, i)),
    [selectedMonday]
  );

  const dayBuckets = useMemo(() => {
    const ownLogs = timelogs
      .filter((l) => l.uuid === currentUser.uuid)
      .sort((a, b) => new Date(a.logtime_utc) - new Date(b.logtime_utc));
    const sessions = pairIntoSessions(ownLogs);

    return weekDayKeys.map((dateStr, i) => {
      const label = new Date(`${dateStr}T00:00:00.000Z`).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
      const daySessions = sessions.filter((s) => s.dayKey === dateStr);
      return { dateStr, label, sessions: daySessions };
    });
  }, [timelogs, currentUser, weekDayKeys]);

  const weekSessions = useMemo(() => dayBuckets.flatMap((d) => d.sessions), [dayBuckets]);

  const rollupText = useMemo(() => {
    if (weekSessions.length === 0) return '';
    const lines = [];
    weekSessions.forEach((s) => {
      if (s.in) {
        const note = s.in.notes ? ` — ${s.in.notes}` : '';
        lines.push(`${toLocalTime(s.in.logtime_utc, displayTz)}: Checked in${note}`);
      }
      if (s.out) {
        const note = s.out.notes ? ` — ${s.out.notes}` : '';
        lines.push(`${toLocalTime(s.out.logtime_utc, displayTz)}: Checked out${note}`);
      }
    });
    return (
      `Make me an end-of-week update based on my SOD/EOD notes this week.\n\n` +
      `${currentUser.name}'s week (${weekRangeLabel}, CST):\n\n` +
      lines.join('\n')
    );
  }, [weekSessions, currentUser, displayTz, weekRangeLabel]);

  const handleDateChange = (value) => {
    const monday = getMondayOfWeek(value, DAY_ANCHOR_TZ);
    if (monday !== value) {
      showError('Please pick a Monday — that\'s the start of the business week.');
      return;
    }
    setSelectedMonday(value);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rollupText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showError("Couldn't copy to clipboard — try selecting the text manually.");
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-6 sm:py-10">
        <Link to="/checkin" className="text-xs font-medium text-bronze hover:text-ink transition-colors">
          ← Back to check-in
        </Link>

        <h1 className="text-lg sm:text-xl font-semibold text-ink mt-3">This week's summary</h1>

        <div className="flex flex-col gap-2 mt-2">
          <span className="inline-flex w-fit items-center gap-1.5 text-[11px] sm:text-xs font-medium text-muted bg-card-tint border border-border rounded-full px-2.5 py-1">
            <IconCalendarWeek size={13} stroke={1.8} className="flex-shrink-0" />
            <span className="whitespace-nowrap">{weekRangeLabel} (CST)</span>
          </span>
          <div className="flex items-center gap-1 w-full">
            <button
              onClick={() => setSelectedMonday((d) => shiftDateString(d, -7))}
              title="Previous week"
              className="flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-lg border border-border text-ink hover:bg-card-tint transition-colors"
            >
              <IconChevronLeft size={14} stroke={1.8} />
            </button>
            <input
              type="date"
              value={selectedMonday}
              onChange={(e) => handleDateChange(e.target.value)}
              max={thisWeekMonday}
              className="flex-1 min-w-0 border border-border rounded-lg px-2 py-1.5 text-xs text-ink"
            />
            <button
              onClick={() => setSelectedMonday((d) => shiftDateString(d, 7))}
              disabled={selectedMonday >= thisWeekMonday}
              title="Next week"
              className="flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-lg border border-border text-ink hover:bg-card-tint transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <IconChevronRight size={14} stroke={1.8} />
            </button>
          </div>
          {selectedMonday !== thisWeekMonday && (
            <button
              onClick={() => setSelectedMonday(thisWeekMonday)}
              className="w-fit text-[11px] sm:text-xs font-medium text-bronze hover:text-ink transition-colors"
            >
              Back to this week
            </button>
          )}
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-sm p-4 sm:p-6 mt-4">
          <div className="divide-y divide-border max-h-[28rem] overflow-y-auto pr-3 scroll-thin">
            {dayBuckets.map((day) => (
              <div key={day.dateStr} className="py-3 first:pt-0 last:pb-0">
                <p className="text-xs sm:text-sm font-semibold text-ink">{day.label}</p>
                {day.sessions.length === 0 ? (
                  <p className="text-[11px] sm:text-xs text-muted italic mt-1">No activity</p>
                ) : (
                  <div className="mt-1.5 space-y-2">
                    {day.sessions.map((s, i) => (
                      <div key={s.in?.log_id || s.out?.log_id || i} className="space-y-1.5">
                        {s.in && (
                          <SessionEntry
                            entry={s.in}
                            label="In"
                            labelClass="text-success-tx"
                            dayLabel={day.label}
                            displayTz={displayTz}
                            onEdit={setEditTarget}
                          />
                        )}
                        {s.out && (
                          <SessionEntry
                            entry={s.out}
                            label="Out"
                            labelClass="text-out-tx"
                            dayLabel={day.label}
                            displayTz={displayTz}
                            onEdit={setEditTarget}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {weekSessions.length > 0 && (
            <button
              onClick={handleCopy}
              className="mt-5 w-full py-2.5 rounded-xl font-medium border border-border text-ink hover:bg-card-tint transition-colors text-xs sm:text-sm px-2"
            >
              {copied ? 'Copied!' : 'Copy AI prompt'}
            </button>
          )}
        </div>
      </div>

      <EditNoteModal
        open={Boolean(editTarget)}
        entry={editTarget?.entry}
        contextLabel={editTarget?.contextLabel}
        onClose={() => setEditTarget(null)}
      />
    </Layout>
  );
}

function SessionEntry({ entry, label, labelClass, dayLabel, displayTz, onEdit }) {
  const timeLabel = toLocalTime(entry.logtime_utc, displayTz);
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${labelClass}`}>{label}</span>
          {entry.notes_edited && <span className="text-[10px] italic text-muted truncate">(note edited)</span>}
        </span>
        <span className="text-[10px] sm:text-xs text-muted flex-shrink-0 whitespace-nowrap">{timeLabel}</span>
      </div>
      {entry.notes && <p className="text-xs sm:text-sm text-ink mt-0.5">{entry.notes}</p>}
      <button
        onClick={() => onEdit({ entry, contextLabel: `${dayLabel} — ${label} at ${timeLabel}` })}
        className="flex items-center gap-1 mt-0.5 text-[11px] font-medium text-bronze hover:text-ink transition-colors"
      >
        <IconPencil size={12} stroke={2} />
        {entry.notes ? 'Edit note' : 'Add note'}
      </button>
    </div>
  );
}
